import { useCallback, useEffect, useState } from "react";
import { Flag, Loader2, ShieldAlert, Users2, ArrowRight, X, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import api from "../api";

const aksiBadge = {
  Edit: "bg-amber-100 text-amber-800 border border-amber-200",
  Hapus: "bg-red-100 text-red-800 border border-red-200",
  Buat: "bg-emerald-100 text-emerald-800 border border-emerald-200",
};
const roleLabel = { owner: "Owner", admin_operasional: "Admin Operasional", kasir: "Kasir", supir: "Supir" };
const fmtWaktu = (iso) => new Date(iso).toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short", timeZone: "Asia/Jakarta" }) + " WIB";
const fmtVal = (v) => (v === null || v === undefined || v === "" ? "—" : Number.isFinite(+v) ? Number(v).toLocaleString("id-ID") : String(v));

export default function AuditTrailPage() {
  const [logs, setLogs] = useState(null);
  const [ringkasan, setRingkasan] = useState(null);
  const [staf, setStaf] = useState("semua");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const loadLogs = useCallback(() => {
    const params = {};
    if (staf !== "semua") params.email = staf;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    api.get("/audit-trail", { params }).then(({ data }) => setLogs(data)).catch(() => setLogs([]));
  }, [staf, dateFrom, dateTo]);

  useEffect(() => { loadLogs(); }, [loadLogs]);
  useEffect(() => {
    api.get("/audit-trail/ringkasan").then(({ data }) => setRingkasan(data)).catch(() => setRingkasan({ per_user: [] }));
  }, []);

  const hasFilter = staf !== "semua" || dateFrom || dateTo;
  const resetFilter = () => { setStaf("semua"); setDateFrom(""); setDateTo(""); };
  const [exporting, setExporting] = useState(false);

  const exportExcel = async () => {
    setExporting(true);
    try {
      const params = {};
      if (staf !== "semua") params.email = staf;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const res = await api.get("/audit-trail/export", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "jejak_audit.xlsx";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Jejak audit diunduh sebagai Excel");
    } catch {
      toast.error("Gagal mengunduh Excel jejak audit");
    } finally {
      setExporting(false);
    }
  };

  const topStaf = ringkasan?.per_user?.[0];

  return (
    <div data-testid="audit-trail-page" className="space-y-6 max-w-6xl">
      <div>
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-zinc-900 flex items-center gap-2">
          <ShieldAlert className="w-7 h-7 text-red-700" /> Laporan Jejak Audit
        </h1>
        <p className="text-sm text-zinc-500 mt-1">
          Pemantau otomatis perubahan data penting (Tonase Muat/Bongkar &amp; Nominal Pengeluaran) oleh staf non-Owner
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="border-zinc-200 shadow-sm">
          <CardContent className="pt-5">
            <p className="text-xs uppercase tracking-wider text-zinc-500">Total Kejadian Tercatat</p>
            <p data-testid="audit-total" className="text-2xl font-bold text-zinc-900 mt-1 tabular-nums">{ringkasan?.total_kejadian ?? "..."}</p>
          </CardContent>
        </Card>
        <Card className="border-red-200 bg-red-50/50 shadow-sm">
          <CardContent className="pt-5">
            <p className="text-xs uppercase tracking-wider text-red-700 flex items-center gap-1"><Flag className="w-3.5 h-3.5" /> Trip Bendera Merah</p>
            <p data-testid="audit-flagged" className="text-2xl font-bold text-red-800 mt-1 tabular-nums">{ringkasan?.trip_bendera_merah ?? "..."}</p>
            <p className="text-xs text-red-700/70 mt-0.5">Tonase diedit &gt; 1 kali</p>
          </CardContent>
        </Card>
        <Card className="border-zinc-200 shadow-sm">
          <CardContent className="pt-5">
            <p className="text-xs uppercase tracking-wider text-zinc-500 flex items-center gap-1"><Users2 className="w-3.5 h-3.5" /> Staf Paling Sering Mengubah</p>
            <p data-testid="audit-top-staf" className="text-base font-bold text-zinc-900 mt-1 truncate">{topStaf ? `${topStaf.nama} (${topStaf.total}x)` : "—"}</p>
            {topStaf && <p className="text-xs text-zinc-500 mt-0.5">{roleLabel[topStaf.role] || topStaf.role}</p>}
          </CardContent>
        </Card>
      </div>

      <Card className="border-zinc-200 shadow-sm">
        <CardHeader className="pb-4">
          <CardTitle className="text-base font-semibold tracking-tight">Peringkat Staf — Perubahan Data Penting</CardTitle>
        </CardHeader>
        <CardContent>
          {!ringkasan ? (
            <div className="flex justify-center py-8 text-zinc-400"><Loader2 className="w-5 h-5 animate-spin" /></div>
          ) : ringkasan.per_user.length === 0 ? (
            <p data-testid="ranking-empty" className="text-sm text-zinc-400 text-center py-6">Belum ada perubahan data penting oleh staf.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="audit-ranking-table">
                <thead>
                  <tr className="border-b border-zinc-200 text-left text-xs uppercase tracking-wider text-zinc-500">
                    <th className="px-3 py-2">#</th><th className="px-3 py-2">Staf</th><th className="px-3 py-2">Peran</th>
                    <th className="px-3 py-2 text-right">Edit</th><th className="px-3 py-2 text-right">Hapus</th>
                    <th className="px-3 py-2 text-right">Total</th><th className="px-3 py-2">Terakhir</th>
                  </tr>
                </thead>
                <tbody>
                  {ringkasan.per_user.map((u, i) => (
                    <tr key={u.email} data-testid={`ranking-row-${u.email}`} className="border-b border-zinc-100">
                      <td className="px-3 py-2.5 text-zinc-400">{i + 1}</td>
                      <td className="px-3 py-2.5 font-medium text-zinc-900">{u.nama}<span className="text-zinc-400 font-normal"> · {u.email}</span></td>
                      <td className="px-3 py-2.5">{roleLabel[u.role] || u.role}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{u.edit}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-red-700">{u.hapus}</td>
                      <td className="px-3 py-2.5 text-right num-hc text-zinc-900">{u.total}</td>
                      <td className="px-3 py-2.5 text-zinc-500 whitespace-nowrap">{fmtWaktu(u.terakhir)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-zinc-200 shadow-sm">
        <CardHeader className="pb-4">
          <CardTitle className="text-base font-semibold tracking-tight">Riwayat Perubahan (Log_Audit)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2" data-testid="audit-filter-bar">
            <Select value={staf} onValueChange={setStaf}>
              <SelectTrigger data-testid="audit-filter-staf" className="bg-white sm:w-64 btn-touch"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="semua">Semua Staf</SelectItem>
                {(ringkasan?.per_user || []).map((u) => (
                  <SelectItem key={u.email} value={u.email}>{u.nama} ({roleLabel[u.role] || u.role})</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex items-center gap-2 min-w-0">
              <Input data-testid="audit-filter-date-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                className="bg-white min-w-0 flex-1 sm:flex-none sm:w-40 input-touch" title="Dari tanggal (WIB)" />
              <span className="text-xs text-zinc-400 shrink-0">s/d</span>
              <Input data-testid="audit-filter-date-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                className="bg-white min-w-0 flex-1 sm:flex-none sm:w-40 input-touch" title="Sampai tanggal (WIB)" />
            </div>
            {hasFilter && (
              <Button data-testid="audit-filter-reset" variant="outline" size="sm" onClick={resetFilter}
                className="border-zinc-300 text-zinc-600 hover:bg-zinc-50 btn-touch">
                <X className="w-4 h-4 mr-1.5" /> Reset
              </Button>
            )}
            <Button data-testid="audit-export-button" variant="outline" size="sm" onClick={exportExcel} disabled={exporting}
              className="border-emerald-200 text-emerald-700 hover:bg-emerald-50 btn-touch">
              {exporting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 mr-1.5" />} Unduh Excel
            </Button>
          </div>
          {logs === null ? (
            <div className="flex justify-center py-8 text-zinc-400"><Loader2 className="w-5 h-5 animate-spin" /></div>
          ) : logs.length === 0 ? (
            <p data-testid="audit-empty" className="text-sm text-zinc-400 text-center py-6">
              {hasFilter ? "Tidak ada catatan audit sesuai filter." : "Belum ada catatan audit."}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="audit-log-table">
                <thead>
                  <tr className="border-b border-zinc-200 text-left text-xs uppercase tracking-wider text-zinc-500">
                    <th className="px-3 py-2">Waktu Kejadian</th><th className="px-3 py-2">User</th>
                    <th className="px-3 py-2">Modul</th><th className="px-3 py-2">Aksi</th>
                    <th className="px-3 py-2">Ref</th><th className="px-3 py-2">Kolom</th>
                    <th className="px-3 py-2">Data Lama → Data Baru</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((l) => (
                    <tr key={l.id} data-testid={`audit-row-${l.id}`} className="border-b border-zinc-100 align-top">
                      <td className="px-3 py-2.5 whitespace-nowrap text-zinc-600">{fmtWaktu(l.Waktu_Kejadian)}</td>
                      <td className="px-3 py-2.5 font-medium text-zinc-900 whitespace-nowrap">{l.Nama_User}</td>
                      <td className="px-3 py-2.5">{l.Modul_Yang_Diubah}</td>
                      <td className="px-3 py-2.5"><Badge className={`${aksiBadge[l.Aksi] || ""} font-medium`}>{l.Aksi}</Badge></td>
                      <td className="px-3 py-2.5 whitespace-nowrap">{l.Ref}</td>
                      <td className="px-3 py-2.5 whitespace-nowrap">{l.Field}</td>
                      <td className="px-3 py-2.5 whitespace-nowrap">
                        <span className="tabular-nums text-zinc-500 line-through">{fmtVal(l.Data_Lama)}</span>
                        <ArrowRight className="w-3.5 h-3.5 inline mx-1.5 text-zinc-400" />
                        <span className="num-hc text-zinc-900">{fmtVal(l.Data_Baru)}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
